// src/pages/Summary.js
import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { apiService } from '../services/apiService';
import './Summary.css';

const PREV_TARGET_API_BASE = 'http://localhost:5134/api';

const Summary = () => {
  const [serverId, setServerId] = useState('2');
  const [productionDate, setProductionDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [pcbData, setPcbData] = useState([]);
  const [emaData, setEmaData] = useState([]);
  const [pcbTableData, setPcbTableData] = useState([]);
  const [emaTableData, setEmaTableData] = useState([]);
  const [error, setError] = useState('');
  const [pcbStats, setPcbStats] = useState({
    totalLines: 0,
    totalProduction: 0,
    totalProjects: 0,
    avgProduction: 0
  });
  const [emaStats, setEmaStats] = useState({
    totalLines: 0,
    totalProduction: 0,
    totalProjects: 0,
    avgProduction: 0
  });
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const serverList = [
    { id: 2, name: 'RCP Jaipur' },
    { id: 3, name: 'HDR 1101' },
    { id: 4, name: 'HDR 3000' },
    { id: 5, name: 'HDR 1100' },
    { id: 6, name: 'Guwahati' },
  ];

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setProductionDate(today);
  }, []);

  // Auto-fetch on initial load when date is set
  useEffect(() => {
    if (productionDate && !initialLoadDone) {
      // Small delay to ensure everything is ready
      const timer = setTimeout(() => {
        fetchSummary();
        setInitialLoadDone(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [productionDate]);

  // ─────────────────────────────────────────────────────────
  // ✅ KEPT — this is only used to decide whether "today's" Shift-B
  // rows should be shown yet (a display/visibility toggle), not to
  // calculate target-till-now math. It's a reasonable global
  // approximation of "is it currently evening/night" and is
  // independent of any individual line's selected shift timing.
  // ─────────────────────────────────────────────────────────
  const getCurrentShift = () => {
    const now = new Date();
    const hours = now.getHours();
    if (hours >= 8 && hours < 19) {
      return 'A';
    } else if (hours >= 20 || hours < 8) {
      return 'B';
    }
    return hours >= 19 && hours < 20 ? 'A' : 'B';
  };

  // ─────────────────────────────────────────────────────────
  // ❌ REMOVED (old fixed 11-hour-assumption versions):
  //   getElapsedHoursInShift, isShiftComplete,
  //   calculateTargetTillNow, calculateStatusPercentage
  //
  // ✅ REPLACED WITH — per-line versions driven by each line's own
  // selected shift timing (item.startTime / item.endTime /
  // item.durationHours, now returned by GetDailyLineSummary via
  // apiService.getLineSummary).
  // ─────────────────────────────────────────────────────────

  const getElapsedHoursForTiming = (startTime, endTime, selectedDate) => {
    if (!startTime || !endTime) return 0;

    const today = new Date().toISOString().split('T')[0];
    if (selectedDate < today) return 0;
    if (selectedDate > today) return 0;

    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();

    const [sH, sM] = startTime.split(':').map(Number);
    const [eH, eM] = endTime.split(':').map(Number);
    const startMin = sH * 60 + sM;
    let   endMin   = eH * 60 + eM;
    const crossesMidnight = endMin <= startMin;
    if (crossesMidnight) endMin += 24 * 60;

    let currentMin = nowMin;
    if (crossesMidnight && nowMin < startMin) currentMin += 24 * 60;

    const elapsedMin = Math.max(0, Math.min(currentMin - startMin, endMin - startMin));
    return elapsedMin / 60;
  };

  const isShiftCompleteForTiming = (startTime, endTime, selectedDate) => {
    const today = new Date().toISOString().split('T')[0];
    if (selectedDate < today) return true;
    if (selectedDate > today) return false;

    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const [sH, sM] = startTime.split(':').map(Number);
    const [eH, eM] = endTime.split(':').map(Number);
    const startMin = sH * 60 + sM;
    let   endMin   = eH * 60 + eM;
    const crossesMidnight = endMin <= startMin;
    if (crossesMidnight) endMin += 24 * 60;

    let currentMin = nowMin;
    if (crossesMidnight && nowMin < startMin) currentMin += 24 * 60;

    return currentMin >= endMin;
  };

  const calculateTargetTillNowForLine = (totalTarget, timing, selectedDate) => {
    if (!timing?.startTime || !timing?.endTime || !timing?.durationHours) {
      // No shift timing saved for this line yet — fall back to full target
      // rather than guessing, so at least the number isn't silently wrong.
      return totalTarget;
    }
    if (isShiftCompleteForTiming(timing.startTime, timing.endTime, selectedDate)) {
      return totalTarget;
    }
    const elapsed = getElapsedHoursForTiming(timing.startTime, timing.endTime, selectedDate);
    const perHour = totalTarget / timing.durationHours;
    return Math.round(perHour * elapsed);
  };

  const calculateStatusPercentageForLine = (currentProduction, targetDefined, timing, selectedDate) => {
    if (!targetDefined || targetDefined === 0) {
      return null;
    }
    if (!timing?.startTime || !timing?.endTime || !timing?.durationHours) {
      return ((currentProduction / targetDefined) * 100).toFixed(2);
    }
    const complete = isShiftCompleteForTiming(timing.startTime, timing.endTime, selectedDate);
    if (complete) {
      return ((currentProduction / targetDefined) * 100).toFixed(2);
    }
    const tillNow = calculateTargetTillNowForLine(targetDefined, timing, selectedDate);
    if (!tillNow || tillNow === 0) return '0.00';
    return ((currentProduction / tillNow) * 100).toFixed(2);
  };

  // ✅ NEW — fallback when no target is entered for the selected date:
  // pulls the most recent previously-entered target for this line.
  const fetchPreviousTarget = async (sId, area, projCode, lineUserId, beforeDate) => {
    try {
      const params = new URLSearchParams({
        serverId: sId, area, projCode, lineNo: lineUserId, beforeDate,
      });
      const r = await fetch(`${PREV_TARGET_API_BASE}/EntryFrom/previoustarget?${params}`, {
        credentials: 'include',
        headers: { accept: '*/*' },
      });
      if (!r.ok) return null;
      const data = await r.json();
      return data?.target ?? null;
    } catch {
      return null;
    }
  };

  const fetchSummary = async () => {
    if (!serverId || !productionDate) {
      setError('Please fill all fields');
      return;
    }

    setLoading(true);
    setError('');
    setPcbData([]);
    setEmaData([]);
    setPcbTableData([]);
    setEmaTableData([]);

    try {
      const today = new Date().toISOString().split('T')[0];
      const isToday = productionDate === today;
      const currentShift = getCurrentShift();

      const [pcbShiftA, pcbShiftB, emaShiftA, emaShiftB] = await Promise.all([
        apiService.getLineSummary({
          serverId: parseInt(serverId),
          area: 'PCB',
          shift: 'A',
          productionDate
        }),
        apiService.getLineSummary({
          serverId: parseInt(serverId),
          area: 'PCB',
          shift: 'B',
          productionDate
        }),
        apiService.getLineSummary({
          serverId: parseInt(serverId),
          area: 'EMA',
          shift: 'A',
          productionDate
        }),
        apiService.getLineSummary({
          serverId: parseInt(serverId),
          area: 'EMA',
          shift: 'B',
          productionDate
        })
      ]);

      let allPcbData = [];
      if (pcbShiftA.success && pcbShiftA.data) {
        allPcbData = [...allPcbData, ...pcbShiftA.data.map(item => ({ ...item, shift: 'A' }))];
      }
      if (pcbShiftB.success && pcbShiftB.data) {
        if (!isToday || currentShift === 'B' || (new Date().getHours() >= 7 && new Date().getHours() < 8)) {
          allPcbData = [...allPcbData, ...pcbShiftB.data.map(item => ({ ...item, shift: 'B' }))];
        }
      }

      let allEmaData = [];
      if (emaShiftA.success && emaShiftA.data) {
        allEmaData = [...allEmaData, ...emaShiftA.data.map(item => ({ ...item, shift: 'A' }))];
      }
      if (emaShiftB.success && emaShiftB.data) {
        if (!isToday || currentShift === 'B' || (new Date().getHours() >= 7 && new Date().getHours() < 8)) {
          allEmaData = [...allEmaData, ...emaShiftB.data.map(item => ({ ...item, shift: 'B' }))];
        }
      }

      const filteredPcb = allPcbData.filter(item => (item.totalProduction || 0) >= 5);
      const filteredEma = allEmaData.filter(item => (item.totalProduction || 0) >= 5);

      const pcbTableRows = await Promise.all(
        filteredPcb.map(async (item) => {
          const targetResult = await apiService.getTargetDetails({
            serverId: parseInt(serverId),
            shift: item.shift,
            area: 'PCB',
            projCode: item.projectCode,
            lineNo: item.lineUserId,
            startDate: productionDate,
            endDate: productionDate
          });

          let targetDefined = targetResult.success && targetResult.data ? targetResult.data.target : null;

          // ✅ NEW — auto-fill from the previous day's target if none was
          // entered for this date (requirement #3)
          if (!targetDefined) {
            targetDefined = await fetchPreviousTarget(
              parseInt(serverId), 'PCB', item.projectCode, item.lineUserId, productionDate);
          }

          // ✅ NEW — this line's own selected shift timing, returned by
          // GetDailyLineSummary (requirement: per-line shift duration math)
          const timing = {
            startTime:     item.startTime,
            endTime:       item.endTime,
            durationHours: item.durationHours,
          };

          const targetTillNow = targetDefined
            ? calculateTargetTillNowForLine(targetDefined, timing, productionDate)
            : null;
          const gap = targetTillNow ? targetTillNow - item.totalProduction : null;
          const statusPercentage = calculateStatusPercentageForLine(
            item.totalProduction, targetDefined, timing, productionDate);

          return {
            area: 'PCB',
            projectCode: item.projectCode,
            lineUsername: item.userName || `Line ${item.lineUserId}`,
            shift: item.shift,
            shiftTimingName: item.shiftTimingName || null,
            targetDefined: targetDefined,
            targetTillNow: targetTillNow,
            currentProduction: item.totalProduction,
            gap: gap,
            statusPercentage: statusPercentage,
            lineUserId: item.lineUserId
          };
        })
      );

      const emaTableRows = await Promise.all(
        filteredEma.map(async (item) => {
          const targetResult = await apiService.getTargetDetails({
            serverId: parseInt(serverId),
            shift: item.shift,
            area: 'EMA',
            projCode: item.projectCode,
            lineNo: item.lineUserId,
            startDate: productionDate,
            endDate: productionDate
          });

          let targetDefined = targetResult.success && targetResult.data ? targetResult.data.target : null;

          if (!targetDefined) {
            targetDefined = await fetchPreviousTarget(
              parseInt(serverId), 'EMA', item.projectCode, item.lineUserId, productionDate);
          }

          const timing = {
            startTime:     item.startTime,
            endTime:       item.endTime,
            durationHours: item.durationHours,
          };

          const targetTillNow = targetDefined
            ? calculateTargetTillNowForLine(targetDefined, timing, productionDate)
            : null;
          const gap = targetTillNow ? targetTillNow - item.totalProduction : null;
          const statusPercentage = calculateStatusPercentageForLine(
            item.totalProduction, targetDefined, timing, productionDate);

          return {
            area: 'EMA',
            projectCode: item.projectCode,
            lineUsername: item.userName || `Line ${item.lineUserId}`,
            shift: item.shift,
            shiftTimingName: item.shiftTimingName || null,
            targetDefined: targetDefined,
            targetTillNow: targetTillNow,
            currentProduction: item.totalProduction,
            gap: gap,
            statusPercentage: statusPercentage,
            lineUserId: item.lineUserId
          };
        })
      );

      setPcbTableData(pcbTableRows);
      setEmaTableData(emaTableRows);
      setPcbData(filteredPcb);
      setEmaData(filteredEma);
      calculateStats(filteredPcb, setPcbStats);
      calculateStats(filteredEma, setEmaStats);

      if (filteredPcb.length === 0 && filteredEma.length === 0) {
        setError('No data found for both PCB and EMA');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data, setStatsFunc) => {
    if (!data || data.length === 0) {
      setStatsFunc({ totalLines: 0, totalProduction: 0, totalProjects: 0, avgProduction: 0 });
      return;
    }

    const uniqueLines = new Set(data.map(item => item.userName || item.lineUserId)).size;
    const uniqueProjects = new Set(data.map(item => item.projectCode)).size;
    const totalProd = data.reduce((sum, item) => sum + (item.totalProduction || 0), 0);
    const avgProd = uniqueLines > 0 ? Math.round(totalProd / uniqueLines) : 0;

    setStatsFunc({
      totalLines: uniqueLines,
      totalProduction: totalProd,
      totalProjects: uniqueProjects,
      avgProduction: avgProd
    });
  };

  const getStatusColor = (statusPercentage) => {
    if (statusPercentage === null) return '#64748b';
    if (statusPercentage >= 90) return '#10b981';
    if (statusPercentage >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const getStatusBadge = (statusPercentage) => {
    if (statusPercentage === null) return '-';
    if (statusPercentage >= 90) return '🟢';
    if (statusPercentage >= 70) return '🟡';
    return '🔴';
  };

  const prepareChartData = (summaryData) => {
    if (!summaryData || summaryData.length === 0) {
      return { categories: [], series: [] };
    }

    const groupedData = {};
    summaryData.forEach(item => {
      const userName = item.userName || `Line ${item.lineUserId}`;
      if (!groupedData[userName]) {
        groupedData[userName] = [];
      }
      groupedData[userName].push({
        projectCode: item.projectCode,
        production: item.totalProduction || 0
      });
    });

    const categories = Object.keys(groupedData);
    const allProjects = [...new Set(summaryData.map(item => item.projectCode))];

    const colorPalette = [
      '#2563eb', '#7c3aed', '#dc2626', '#059669', '#d97706',
      '#0891b2', '#4f46e5', '#be123c', '#0d9488', '#c026d3'
    ];

    const series = allProjects.map((project, index) => {
      const data = categories.map(userName => {
        const userProjects = groupedData[userName];
        const projectData = userProjects.find(p => p.projectCode === project);
        return projectData ? projectData.production : 0;
      });

      return {
        name: project,
        data: data,
        color: colorPalette[index % colorPalette.length]
      };
    });

    return { categories, series };
  };

  const getChartOptions = (summaryData, stats, areaName) => {
    const { categories, series } = prepareChartData(summaryData);

    return {
      chart: {
        type: 'column',
        height: 350,
        backgroundColor: '#ffffff',
        style: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        }
      },
      title: {
        text: `${areaName} Production`,
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: '700',
          color: '#1e293b'
        }
      },
      subtitle: {
        text: `Total: ${stats.totalProduction.toLocaleString()}`,
        align: 'left',
        style: {
          fontSize: '12px',
          color: '#64748b'
        }
      },
      xAxis: {
        categories: categories,
        title: {
          text: 'Line',
          style: {
            fontSize: '11px',
            fontWeight: '600',
            color: '#1e293b'
          }
        },
        labels: {
          rotation: -45,
          style: {
            fontSize: '10px',
            fontWeight: '600',
            color: '#334155'
          }
        },
        lineWidth: 2,
        lineColor: '#cbd5e1'
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Production',
          style: {
            fontSize: '11px',
            fontWeight: '600',
            color: '#1e293b'
          }
        },
        labels: {
          style: {
            fontSize: '10px',
            fontWeight: '600',
            color: '#334155'
          },
          formatter: function() {
            return this.value.toLocaleString();
          }
        },
        stackLabels: {
          enabled: true,
          style: {
            fontSize: '10px',
            fontWeight: '700',
            color: '#1e293b',
            textOutline: '1px white'
          },
          formatter: function() {
            return this.total > 0 ? this.total.toLocaleString() : '';
          }
        },
        gridLineWidth: 1,
        gridLineColor: '#e2e8f0'
      },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: {
          fontSize: '10px',
          fontWeight: '600',
          color: '#334155'
        },
        symbolHeight: 8,
        symbolWidth: 8,
        symbolRadius: 2,
        itemMarginBottom: 3
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#cbd5e1',
        borderRadius: 6,
        borderWidth: 1,
        shadow: true,
        useHTML: true,
        headerFormat: '<div style="font-size:11px;font-weight:700;color:#1e293b;margin-bottom:4px;">{point.x}</div>',
        pointFormat: '<div style="font-size:10px;margin:2px 0;"><span style="color:{series.color};font-weight:700;">● {series.name}</span>: <b>{point.y:,.0f}</b></div>',
        shared: false,
        style: {
          fontSize: '10px'
        }
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            formatter: function() {
              const columnTotal = this.point.stackTotal || 0;
              const percentage = (this.y / columnTotal) * 100;
              return (this.y > 0 && percentage > 8) ? this.y.toLocaleString() : '';
            },
            style: {
              fontSize: '9px',
              fontWeight: '700',
              color: '#ffffff',
              textOutline: '1px rgba(0, 0, 0, 0.5)'
            },
            crop: false,
            overflow: 'allow'
          },
          borderWidth: 1,
          borderColor: '#ffffff',
          pointPadding: 0.1,
          groupPadding: 0.15
        }
      },
      series: series,
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      }
    };
  };

  const renderCompactTable = (tableData, areaName) => {
    if (tableData.length === 0) return null;

    return (
      <div className="compact-table-card">
        <div className="compact-table-header">
          <h3>{areaName} Tracking</h3>
        </div>
        <div className="compact-table-wrapper">
          <table className="compact-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Line</th>
                <th>Sh</th>
                <th>Timing</th>
                <th>Target</th>
                <th>Till Now</th>
                <th>Current</th>
                <th>Gap</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={index}>
                  <td><span className="compact-project">{row.projectCode}</span></td>
                  <td>{row.lineUsername}</td>
                  <td><span className={`compact-shift shift-${row.shift}`}>{row.shift}</span></td>
                  <td>
                    {row.shiftTimingName
                      ? <span style={{ fontSize: '11px', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>
                          {row.shiftTimingName}
                        </span>
                      : <span className="na">Not set</span>
                    }
                  </td>
                  <td>{row.targetDefined !== null ? row.targetDefined.toLocaleString() : <span className="na">N/A</span>}</td>
                  <td>{row.targetTillNow !== null ? row.targetTillNow.toLocaleString() : <span className="na">-</span>}</td>
                  <td><strong>{row.currentProduction.toLocaleString()}</strong></td>
                  <td>
                    {row.gap !== null
                      ? <span className={row.gap > 0 ? 'gap-neg' : 'gap-pos'}>
                          {row.gap > 0 ? '-' : '+'}{Math.abs(row.gap).toLocaleString()}
                        </span>
                      : <span className="na">-</span>
                    }
                  </td>
                  <td>
                    {row.statusPercentage !== null
                      ? <span className="status-compact" style={{ color: getStatusColor(row.statusPercentage) }}>
                          {getStatusBadge(row.statusPercentage)} {row.statusPercentage}%
                        </span>
                      : <span className="na">-</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="summary-container">
      {/* Combined Header and Filters in One Line */}
      <div className="summary-header">
        <div className="summary-title-row">
          <div className="summary-left">
            <div className="summary-icon">📊</div>
            <div className="summary-text">
              <h1 className="page-title">Production Summary</h1>
              <p className="page-subtitle">Real-time production tracking & analytics</p>
            </div>
          </div>

          <div className="summary-filters">
            <div className="filter-group-inline">
              <label>Plant</label>
              <select
                value={serverId}
                onChange={(e) => setServerId(e.target.value)}
                disabled={loading}
              >
                <option value="">Select Server</option>
                {serverList.map((server) => (
                  <option key={server.id} value={server.id}>
                    {server.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group-inline">
              <label>Production Date</label>
              <input
                type="date"
                value={productionDate}
                onChange={(e) => setProductionDate(e.target.value)}
                disabled={loading}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            <button
              className="fetch-btn"
              onClick={fetchSummary}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="btn-spinner"></div>
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <span>🔍</span>
                  <span>Fetch Summary</span>
                </>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            ❌ {error}
          </div>
        )}
      </div>

      {/* Side by Side Tables */}
      {(emaTableData.length > 0 || pcbTableData.length > 0) && (
        <div className="tables-grid">
          {emaTableData.length > 0 && renderCompactTable(emaTableData, 'EMA')}
          {pcbTableData.length > 0 && renderCompactTable(pcbTableData, 'PCB')}
        </div>
      )}

      {/* Side by Side Charts */}
      {(emaData.length > 0 || pcbData.length > 0) && (
        <div className="charts-grid">
          {/* EMA Chart */}
          {emaData.length > 0 && (
            <div className="chart-section">
              <div className="mini-stats">
                <div className="mini-stat">
                  <span className="mini-icon">👥</span>
                  <div>
                    <div className="mini-value">{emaStats.totalLines}</div>
                    <div className="mini-label">Total Lines</div>
                  </div>
                </div>
                  <div className="mini-stat">
                  <span className="mini-icon">🎯</span>
                  <div>
                    <div className="mini-value">{emaStats.totalProjects}</div>
                    <div className="mini-label">Total Projects</div>
                  </div>
                </div>
                <div className="mini-stat">
                  <span className="mini-icon">📦</span>
                  <div>
                    <div className="mini-value">{emaStats.totalProduction.toLocaleString()}</div>
                    <div className="mini-label">Total Production</div>
                  </div>
                </div>
              </div>
              <div className="chart-card">
                <HighchartsReact
                  highcharts={Highcharts}
                  options={getChartOptions(emaData, emaStats, 'EMA')}
                />
              </div>
            </div>
          )}

          {/* PCB Chart */}
          {pcbData.length > 0 && (
            <div className="chart-section">
              <div className="mini-stats">
                <div className="mini-stat">
                  <span className="mini-icon">👥</span>
                  <div>
                    <div className="mini-value">{pcbStats.totalLines}</div>
                    <div className="mini-label">Total Lines</div>
                  </div>
                </div>
                  <div className="mini-stat">
                  <span className="mini-icon">🎯</span>
                  <div>
                    <div className="mini-value">{pcbStats.totalProjects}</div>
                    <div className="mini-label">Total Projects</div>
                  </div>
                </div>
                <div className="mini-stat">
                  <span className="mini-icon">📦</span>
                  <div>
                    <div className="mini-value">{pcbStats.totalProduction.toLocaleString()}</div>
                    <div className="mini-label">Total Production</div>
                  </div>
                </div>
              </div>
              <div className="chart-card">
                <HighchartsReact
                  highcharts={Highcharts}
                  options={getChartOptions(pcbData, pcbStats, 'PCB')}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading State on Initial Load */}
      {loading && pcbData.length === 0 && emaData.length === 0 && (
        <div className="loading-state">
          <div className="loading-spinner-large"></div>
          <h3>Loading Production Data...</h3>
          <p>Please wait while we fetch the latest production summary</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && pcbData.length === 0 && emaData.length === 0 && !error && (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No Data Available</h3>
          <p>Select server and date, then click "Fetch Summary" to view production data</p>
        </div>
      )}
    </div>
  );
};

export default Summary;